import * as THREE from "three";
import { inked, toon } from "../toon";
import { closeSeam, smooth } from "./geom";
import { buildHatHair } from "./head-natural";

// The natural face's cowboy hat (#75, the realistic rider): a felt hat past
// the toy's cylinder and ball. Head space, face +z, the same footprint as
// the toy hat so the hair under it still fits: a cattleman crown with a
// crease down the top and pinches either side of the front, a leather band,
// and a brim with a rolled edge that curls up at the sides and dips a little
// front and back. Three draws, one fewer than the toy's.

/** How the crown is squashed: narrower across (x) than front to back (z), like the toy's. */
const CROWN_SCALE = { x: 0.95, z: 1.1 };
/** Where the crown's side meets the brim, and its radius there. */
const CROWN_BASE = { y: 0.145, r: 0.19 };
/** The crown's side and top, base up to the middle of the top (lathe radius, height). */
const CROWN: [number, number][] = [
  [CROWN_BASE.r, CROWN_BASE.y],
  [0.187, 0.19],
  [0.179, 0.24],
  [0.168, 0.285],
  [0.158, 0.308],
  [0.14, 0.321],
  [0.1, 0.328],
  [0.05, 0.331],
  [0.001, 0.332],
];
/** The crease down the top, front to back: its depth at the middle and half-width across. */
export const CREASE = { depth: 0.045, width: 0.05 };
/** How much the pinches narrow the crown's upper front. */
const PINCH = 0.24;
/** The brim: where it leaves the crown, how far out it reaches, its thickness. */
const BRIM = { r0: 0.165, r1: 0.35, thick: 0.012, y: 0.15 };
/** How far the brim's sides curl up at the rim, and its front and back dip. */
export const BRIM_CURL = { up: 0.075, dip: 0.018 };
const SEGMENTS = 40;

/** The crown: lathed, then creased down the top and pinched at the front. */
export function crownGeometry(): THREE.BufferGeometry {
  const geo = new THREE.LatheGeometry(
    CROWN.map(([r, y]) => new THREE.Vector2(r, y)),
    SEGMENTS,
  );
  const pos = geo.getAttribute("position");
  const top = CROWN[CROWN.length - 1]![1];
  for (let i = 0; i < pos.count; i++) {
    let x = pos.getX(i);
    let y = pos.getY(i);
    const z = pos.getZ(i);
    const rho = Math.hypot(x, z);
    // The crease: deepest on the middle line, fading out before the rim.
    const inTop = smooth((0.17 - rho) / 0.08) * smooth((y - 0.29) / 0.03);
    y -= CREASE.depth * Math.exp(-((x / CREASE.width) ** 2)) * inTop * (y / top);
    // The pinches: the upper front squeezed in from both sides, to a teardrop from above.
    if (z > 0 && rho > 1e-4) x *= 1 - PINCH * smooth((y - 0.2) / 0.11) * (z / rho) ** 3;
    pos.setXYZ(i, x * CROWN_SCALE.x, y, z * CROWN_SCALE.z);
  }
  closeSeam(geo, CROWN.length, SEGMENTS);
  return geo;
}

/** The brim: a closed profile (top, a rolled edge, underside), curled up at the sides. */
export function brimGeometry(): THREE.BufferGeometry {
  const { r0, r1, thick } = BRIM;
  const h = thick / 2;
  const profile: [number, number][] = [
    [r0, h],
    [0.25, h],
    [r1 - 0.02, h * 0.8],
    [r1 - 0.004, h * 0.2], // the roll
    [r1, -h * 0.5],
    [r1 - 0.012, -h],
    [0.25, -h],
    [r0, -h],
    [r0, h],
  ];
  // Wound underside out, then over the top back in: lathe faces point to the right of the profile's direction.
  const geo = new THREE.LatheGeometry(
    profile.reverse().map(([r, y]) => new THREE.Vector2(r, y)),
    SEGMENTS,
  );
  const pos = geo.getAttribute("position");
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    let y = pos.getY(i);
    const z = pos.getZ(i);
    const rho = Math.hypot(x, z);
    const out = smooth((rho - 0.2) / (r1 - 0.2)) ** 1.5;
    const side = rho > 1e-4 ? (x / rho) ** 2 : 0;
    y += BRIM_CURL.up * side * out * out - BRIM_CURL.dip * (1 - side) * out;
    pos.setXYZ(i, x * 0.9, y, z * 1.08);
  }
  closeSeam(geo, profile.length, SEGMENTS);
  return geo;
}

/** The band round the crown's base, a hair proud of the felt. */
function bandGeometry(): THREE.BufferGeometry {
  const geo = new THREE.LatheGeometry([new THREE.Vector2(CROWN_BASE.r + 0.004, CROWN_BASE.y + 0.002), new THREE.Vector2(0.1875 + 0.004, 0.176)], SEGMENTS);
  geo.scale(CROWN_SCALE.x, 1, CROWN_SCALE.z);
  return geo;
}

/** The realistic cowboy hat with the natural face's strand hair under it. */
export function buildNaturalCowboyHat(color: string, hair: string): THREE.Group {
  const hat = new THREE.Group();
  hat.name = "cowboy-hat";
  const felt = toon(color, { roughness: 0.85 }, "cloth");
  const crown = inked(crownGeometry(), felt, 0.015);
  crown.name = "crown";
  hat.add(crown);
  const band = new THREE.Mesh(bandGeometry(), toon("#2a1a12", { roughness: 0.6 }, "leather"));
  band.name = "hat-band";
  hat.add(band);
  const brim = inked(brimGeometry(), toon(color, { roughness: 0.85 }, "cloth"), 0.012);
  brim.name = "brim";
  brim.position.y = BRIM.y;
  hat.add(brim);
  hat.add(buildHatHair(hair));
  return hat;
}
